import {
    Button,
    Form,
    FormLayout,
    Frame,
    Layout,
    Page,
    TextField,
    LegacyCard,
    PageActions,
    Banner,
    DescriptionList,
    Thumbnail,
    List,
    LegacyStack,
    Select,
    Text,
    Checkbox,
} from "@shopify/polaris";
import { useCallback, useEffect, useState } from "react";
import { cloneDeep } from 'lodash';
import { useLocation, useParams } from 'react-router-dom';
import Api from '../../helpers/api';
import debounce from 'lodash.debounce';
import CustomerOnHold from "../customers/CustomerOnHold";
import jwtDecode from "jwt-decode";
import * as helpers from './helpers';
import {getErrorMessage} from "../../helpers/utils";
import {DatePickerModal, toDate} from "../../Dates";

const AddressDetails = ({address}) => {
    const parts = [
        `${address?.company || ''}`,
        `${address?.address1 || ''}`,
        `${address?.address2 || ''}`,
        `${address?.city || ''} - ${address?.state || ''} (${address?.zip_code || ''})`,
        `${address?.country || ''}`,
    ].filter(str => /^[a-z0-9]/i.test(str.trim()));

    return (parts.length)
        ? (<span>{parts.join(', ')}</span>)
        : (<span>(Not Defined)</span>)
}

const PriceMarker = ({ price, strikethrough }) => {
    const style = strikethrough
        ? { textDecoration: 'line-through' }
        : {};

    return (
        <Text variant="headingMd" as="h2">
            <span style={style}>${price}</span>
        </Text>
    );
}

const FinalPrice = ({ lineItem, children }) => {
    const [showInput, setShowInput] = useState(false);
    const onClick = useCallback(() => {
        if (! lineItem.price.locked) {
            setShowInput(true);
        }
    }, []);

    let discounted = lineItem.price.discounted || '0.00';

    if (lineItem.price.discount && lineItem.price.discount.quantity > 0 && discounted > 0) {
        let totalDiscount = lineItem.price.discount.total;
        discounted = discounted - parseFloat((totalDiscount / lineItem.price.discount.quantity).toFixed(2));
    }

    const prices = Array
        .from(new Set([
            parseFloat(lineItem.price.unit || '0.00').toFixed(2),
            parseFloat(discounted).toFixed(2),
        ]))
        .map((price, i, list) => ({
            price,
            strikethrough: i == 0 && list.length > 1,
        }));

    if (! showInput) {
        const style = lineItem.price.locked
            ? {}
            : { cursor: 'pointer' };

        return (
            <div onClick={onClick} title="Enter custom price" style={style}>
                {prices.map((props, i) => <PriceMarker {...props} key={i} />)}
            </div>
        );
    }

    return (
        <>
            {children}
        </>
    );
}

const DiscountFootnote = ({discount}) => {
    const { total } = discount;

    return (total)
        ? (<span>Discount Code: ${total}</span>)
        : null;
}

const Orders = () => {
    const search = useLocation().search;
    const {customerid} = useParams();
    const [locationId] = useState(
        (new URLSearchParams(search)).get('location_id') || null
    );
    const [adminUserId, setAdminUserId] = useState(null);

    const [customer, setCustomer] = useState(null);
    const [location, setLocation] = useState(null);
    const [lineItems, setLineItems] = useState([]);
    const [additionalInfo, setAdditionalInfo] = useState({
        order_notes: '',
        customer_notes: '',
        ship_date: null,
    });
    const [searchInput, setSearchInput] = useState('');
    const [error, setError] = useState(false);
    const [isSearching, setSearching] = useState(false);
    const [errors, setErrors] = useState({});
    const [hasMoreResults, setHasMoreResults] = useState(false);
    const [creating, setCreating] = useState(false);
    const [calculating, setCalculating] = useState(false);
    const [discountCode, setDiscountCode] = useState({
        code: '',
        requires_refresh: false,
        loading: false,
        applied: false,
        fetched_at: 0,
        id: null,
    });
    const [totalPrice, setTotalPrice] = useState(0);
    const [giftWrapVisible, setGiftWrapVisible] = useState({});

    const [isCalculateVisible, setIsCalculateVisible] = useState(false);
    const [isPlaceOrderVisible, setIsPlaceOrderVisible] = useState(false);

    const [threshold, setThreshold] = useState(0);
    const [giftProduct, setGiftProduct] = useState(null);
    const [eligibleForGift, setEligibleForGift] = useState(false);

    const applyDiscountCode = useCallback(() => {
        setDiscountCode(state => ({
            ...state,
            requires_refresh: false,
            loading: true,
            applied: true,
        }));
    }, []);

    const removeDiscountCode = useCallback(() => {
        setDiscountCode(state => ({
            ...state,
            code: '',
            requires_refresh: false,
            loading: true,
            applied: false,
        }));
    }, []);

    useEffect(() => {
        if (discountCode.loading) {
            setIsPlaceOrderVisible(false);
            setIsCalculateVisible(true);
            calculateOrder();
        }
    }, [discountCode.loading]);

    useEffect(() => {
        if (lineItems.some(item => parseInt(item.quantity) > 0)) {
            setIsCalculateVisible(true);
            setIsPlaceOrderVisible(false);
        } else {
            setIsCalculateVisible(false);
            setIsPlaceOrderVisible(false);
        }
    }, [lineItems]);

    useEffect(() => {
        const updatedVisible = { ...giftWrapVisible };

        lineItems.forEach((item) => {
            if (!updatedVisible[item.id]) {
                updatedVisible[item.id] = {};
            }
            [...Array(parseInt(item.quantity)).keys()].forEach((i) => {
                if (!updatedVisible[item.id][i]) {
                    updatedVisible[item.id][i] = false;
                }
            });
        });

        setGiftWrapVisible(updatedVisible);
    }, [lineItems]);

    useEffect(() => {
        const fetchThresholdAndGift = async () => {
            try {
                const { data } = await Api.getThresholdAndGift();
                setThreshold(data.data.threshold || 0);
                setGiftProduct(data.data.giftProduct || null);
            } catch (error) {
                console.error("Failed to fetch threshold and gift:", error);
            }
        };

        fetchThresholdAndGift();
    }, []);

    useEffect(() => {
        setEligibleForGift(totalPrice > threshold && !!giftProduct);
    }, [totalPrice, threshold, giftProduct]);

    //This is trick that Rado provided, ask him, I don't know how it works :)
    //This function basically rounds up/down quantity to closest step
    const debounceQuantityUpdate = useCallback(debounce((key, quantity) => {
        setLineItems((lineItems) => {
            let clone = cloneDeep(lineItems);

            if (lineItems[key].increment_quantity_by)
            {
                const step = clone[key].increment_quantity_by;

                if (quantity > 0 && quantity < step) {
                    quantity = step;
                } else if ((quantity % step) < (step / 2)) {
                    quantity = quantity - (quantity % step);
                } else {
                    quantity = parseInt(quantity) + parseInt(step - (quantity % step));
                }

                clone[key].quantity = quantity.toString();
                clone[key].price.total = helpers.calculateTotalPrice(clone[key]).toFixed(2);
            }

            return clone;
        });

        setDiscountCode(state => ({
            ...state,
            requires_refresh: true,
            applied: false,
        }));

    }, 500), [lineItems]);

    const updateQuantity = useCallback((key, quantity) => {
        if (quantity == '') {
            quantity = 0;
        }

        quantity = parseInt(quantity);

        if (lineItems[key].inventory_quantity < quantity) {
            return;
        }

        let clone = cloneDeep(lineItems);

        clone[key].quantity = quantity.toString();
        clone[key].price.total = helpers.calculateTotalPrice(clone[key]).toFixed(2);

        setLineItems(clone);

        const totalPrice = clone.reduce((acc, item) => {
            return acc + parseFloat(item.price.total);
        }, 0.0);
        setTotalPrice(totalPrice);

        setDiscountCode(state => ({
            ...state,
            requires_refresh: true,
        }));

        setIsPlaceOrderVisible(false);
        setIsCalculateVisible(true);

        debounceQuantityUpdate(key, quantity);
    }, [lineItems, location]);

    const calculateOrder = async () => {
        setCalculating(true);
        try {
            const response = await placeDraftOrder(true);
            setTotalPrice(response?.totalPrice || 0);
            setDiscountCode(state => ({
                ...state,
                loading: false,
            }));
            setIsCalculateVisible(false);
            setIsPlaceOrderVisible(true);
        } catch (error) {
            shopify.toast.show(getErrorMessage(error), { isError: true });
            setDiscountCode(state => ({
                ...state,
                loading: false,
            }));
            setIsPlaceOrderVisible(false);
            setIsCalculateVisible(true);
        } finally {
            setCalculating(false);
        }
    };

    const toggleGiftWrap = (itemId, itemIndex) => {
        setGiftWrapVisible(prevState => ({
            ...prevState,
            [itemId]: {
                ...prevState[itemId],
                [itemIndex]: !prevState[itemId]?.[itemIndex],
            }
        }));

        setLineItems(prevLineItems => {
            const updatedLineItems = [...prevLineItems];
            const lineItemIndex = updatedLineItems.findIndex(item => item.id === itemId);
            const lineItem = updatedLineItems[lineItemIndex];

            if (!Array.isArray(lineItem.gift_wrapped)) {
                lineItem.gift_wrapped = new Array(parseInt(lineItem.quantity)).fill('none');
            }
            if (!Array.isArray(lineItem.gift_wrap_note)) {
                lineItem.gift_wrap_note = new Array(parseInt(lineItem.quantity)).fill('');
            }

            if (!giftWrapVisible[itemId]?.[itemIndex]) {
                lineItem.gift_wrapped[itemIndex] = 'flat-packed';
            } else {
                lineItem.gift_wrapped[itemIndex] = 'none';
                lineItem.gift_wrap_note[itemIndex] = '';

                const allNone = lineItem.gift_wrapped.every(value => value === 'none');

                if (allNone) {
                    lineItem.gift_wrapped = 'none';
                    lineItem.gift_wrap_note = '';
                }
            }

            updatedLineItems[lineItemIndex] = lineItem;

            return updatedLineItems;
        });

        setIsPlaceOrderVisible(false);
        setIsCalculateVisible(true);
    };

    const [notesErrors, setNotesErrors] = useState({});

    const updateLineItemWrapAndNote = (itemId, itemIndex, key, value) => {
        if (!['gift-wrapped', 'flat-packed'].includes(value)) {
            const cleanedValue = value.replace(/[^a-zA-Z0-9.,\s\r\n]/g, '');
            if (value !== cleanedValue) {
                setNotesErrors(prevErrors => ({
                    ...prevErrors,
                    [itemId]: {
                        ...prevErrors[itemId],
                        [itemIndex]: true
                    }
                }));

                setTimeout(() => {
                    setNotesErrors(prevErrors => ({
                        ...prevErrors,
                        [itemId]: {
                            ...prevErrors[itemId],
                            [itemIndex]: false
                        }
                    }));
                }, 3000);
            }
            value = cleanedValue;
        }

        setLineItems(prevLineItems => {
            const updatedLineItems = [...prevLineItems];
            const lineItemIndex = updatedLineItems.findIndex(item => item.id === itemId);
            const lineItem = updatedLineItems[lineItemIndex];

            if (!Array.isArray(lineItem[key])) {
                lineItem[key] = new Array(parseInt(lineItem.quantity)).fill('');
            }

            const updatedArray = [...lineItem[key]];
            updatedArray[itemIndex] = value;
            lineItem[key] = updatedArray;
            updatedLineItems[lineItemIndex] = lineItem;

            return updatedLineItems;
        });
    };

    const updateManualPrice = useCallback((key, price) => {
        let clone = cloneDeep(lineItems);

        if (price < 0) {
            price = 0;
        } else if (parseFloat(price) > parseFloat(clone[key].price.unit)) {
            price = clone[key].price.unit;
        }

        clone[key].price.manual = price.toString();
        clone[key].price.total = helpers.calculateTotalPrice(clone[key]).toFixed(2);

        setLineItems(clone);

        setDiscountCode(state => ({
            ...state,
            requires_refresh: true,
        }));

        setIsPlaceOrderVisible(false);
        setIsCalculateVisible(true);
    }, [lineItems, location]);

    // Getting the customer from the router parameter.
    useEffect(() => {
        shopify.idToken().then(sessionToken => {
            const decodedToken = jwtDecode(sessionToken);

            if (decodedToken['sub']) {
                const id = decodedToken['sub'];
                setAdminUserId(id);
            }
        });
        Promise
            .all([
                Api.getCustomer(customerid).then(item => item.data),
                Api.getLocation(locationId).then(item => item.data),
            ])
            .then(([customerData, locationData]) => {
                setCustomer(customerData.data);
                setLocation(locationData.data);
                setAdditionalInfo({
                    ...additionalInfo,
                    customer_notes: locationData.data.notes,
                });
            })
    }, []);

    const searchProducts = async (event) => {
        event.preventDefault();

        if ((searchInput.length > 0 && searchInput.length < 3) || searchInput.length == 0) {
            setError(true);
            return;
        }

        setError(false);
        setSearching(true);
        const { data } = await Api.getProducts(searchInput, locationId)
        const selectedLineItems = [...lineItems].filter(item => parseInt(item.quantity, 10) > 0)

        setHasMoreResults(data.meta.last_page > 1);

        const updatedLineItems = data.data.filter(item => ! selectedLineItems.some(({ id }) => id == item.shopify_variant_id))
            .map(variant => ({
                id: variant.shopify_variant_id,
                shopify_product_id: variant.shopify_product_id,
                quantity: "0",
                title: `${variant.sku} - ${variant.product_title}`,
                price: {
                    unit: variant.price,
                    discounted: variant.price,
                    total: 0,
                    manual: null,
                    locked: variant.price_list,
                    price_list: variant.price_list,
                    original: variant.original_price,
                },
                image_url: variant.image_url,
                inventory_quantity: variant.inventory_quantity,
                options: variant.options,
                increment_quantity_by : variant.increment_quantity_by,
                discount_code: null,
                gift_wrapped: 'none',
                gift_wrap_note: '',
            })).concat(selectedLineItems);

        setLineItems(updatedLineItems);
        setSearching(false);
    }

    const splitAndCombineLineItemsByWrapAndNote = (lineItems) => {
        return lineItems.flatMap(item => {
            const quantity = parseInt(item.quantity, 10);
            if (Array.isArray(item.gift_wrapped)) {
                const allNone = item.gift_wrapped.every(value => value === 'none');
                const allNotesEmpty = item.gift_wrap_note.every(note => note === '');
                if (!(allNone && allNotesEmpty)) {
                    return [...Array(quantity).keys()].map((_, i) => {
                        const customAttributes = [...(item.custom_attributes || [])];
                        return {
                            ...item,
                            quantity: 1,
                            gift_wrapped: item.gift_wrapped[i],
                            gift_wrap_note: item.gift_wrap_note ? item.gift_wrap_note[i] : '',
                            custom_attributes: customAttributes.length > 0 ? customAttributes : undefined,
                        };
                    });
                }
            }
            return [{
                ...item,
                quantity: quantity,
                gift_wrapped: 'none',
                gift_wrap_note: '',
            }];
        });
    };

    const placeDraftOrder = async (preview = false) => {
        const submitErrors = {};

        const processedItems = splitAndCombineLineItemsByWrapAndNote(lineItems);

        const variants = processedItems
            .filter(item => item.quantity > 0)
            .map(item => ({
                id: item.id,
                quantity: item.quantity,
                custom_attributes: item.custom_attributes,
                product_id: item.shopify_product_id,
                original_price: parseFloat(item.price.original),
                manual_price: parseFloat(item.price.manual),
                gift_wrapped: item.gift_wrapped,
                gift_wrap_note: item.gift_wrap_note,
            }));

        const draftOrderData = {
            variants,
            location_id: location.id,
            total_price: processedItems.reduce((acc, item) => {
                return acc + parseFloat(item.price.total) - parseFloat(item.discount_code?.total || '0');
            }, 0.0),
            customer_notes: location.notes,
            billing_address: helpers.formatLocationAddress(location?.billing ?? customer?.default_location?.billing, customer, location?.phone),
            shipping_address: helpers.formatLocationAddress(location?.shipping, customer, location?.phone),
            shipping_method: {
                name: "Call Center - Best Rate",
                price: 0,
                key: "call-center-shipping",
            },
            user_id: adminUserId,
            discount_codes: discountCode.code,
            gift: (eligibleForGift && giftProduct) ? giftProduct : null,
            preview: preview,
            ...additionalInfo,
        };

        try {
            let { data } = await Api.placeDraftOrder(draftOrderData);
            if (!preview) {
                Api.updateLocationAfterOrder(location.id, {
                    notes: additionalInfo.customer_notes,
                });
            }

            return data.data;

        } catch (error) {
            if (error.response.status == 422) {
                Object.keys(error.response.data.errors).forEach(key => {
                    submitErrors[key] = error.response.data.errors[key][0];
                });
                setErrors(submitErrors);
            } else {
                setCreating(false);
                throw error;
            }
        }

        return null;
    };

    const placeAndReview = async () => {
        setCreating(true);

        const draft = await placeDraftOrder();

        if (draft) {
            setTimeout(() => {
                const id = `${draft.id}`.replace(/^.*\/(\d+)$/, '$1');
                open(`shopify://admin/draft_orders/${id}`, '_top');

                setCreating(false);
            }, 5000);

            return;
        }

        setCreating(false);
    }

    const changeAdditionalInfo = useCallback((value, id) => {
        setAdditionalInfo({
            ...additionalInfo,
            [id]: value,
        });
    }, [additionalInfo]);

    return (
        <Frame>
            <Page title={`Place Order`}>
                <Layout>
                    <CustomerOnHold customer={customer}/>

                    <Layout.Section>
                    <LegacyCard title={`Customer Account`} sectioned>
                        <DescriptionList items={[
                            {term: 'Email', description: location?.shopify_customer_email || location?.tracking_email || customer?.email },
                            {term: 'Phone', description: location?.phone_number || customer?.phone_number },
                            {term: 'Name', description: `${customer?.additional_data.first_name} ${customer?.additional_data.last_name}` },
                            {term: 'Shipping', description: <AddressDetails address={location?.shipping} /> },
                            {term: 'Billing', description: <AddressDetails address={location?.billing || customer?.default_location?.billing} /> },
                        ]} />
                        <LegacyCard.Section>
                            <FormLayout>
                                <TextField multiline={3} label="Customer Notes" value={additionalInfo.customer_notes} id="customer_notes" onChange={changeAdditionalInfo} />
                                <DatePickerModal
                                    buttonLabel="Ship Date"
                                    startDate={additionalInfo.ship_date}
                                    onChange={val => changeAdditionalInfo(toDate(val), 'ship_date')}
                                />
                            </FormLayout>
                        </LegacyCard.Section>
                    </LegacyCard>
                    </Layout.Section>
                    <Layout.Section>
                        <LegacyCard title="Line Items">
                            <LegacyCard.Section>
                                <Form onSubmit={searchProducts}>
                                    <FormLayout>
                                            <TextField label="Search" value={searchInput} onChange={setSearchInput} />
                                            {error && (
                                                <Text tone="critical">
                                                    Search must have at least 3 characters.
                                                </Text>
                                            )}
                                            <Button  submit loading={isSearching} variant="primary">Search</Button>
                                    </FormLayout>
                                </Form>
                            </LegacyCard.Section>
                            {lineItems.length > 0 && (
                                <>
                                    <LegacyCard.Section title="Line Items">
                                        <table className="Polaris-DataTable__Table" style={{width: '100%'}}>
                                            <thead>
                                                <tr>
                                                    <th className="Polaris-DataTable__Cell" colSpan="2" style={{ textAlign: 'left'}}>Item</th>
                                                    <th className="Polaris-DataTable__Cell" style={{ textAlign: 'left',}}>Stock</th>
                                                    <th className="Polaris-DataTable__Cell" style={{ textAlign: 'left', width: '80px' }}>Quantity</th>
                                                    <th className="Polaris-DataTable__Cell" style={{ textAlign: 'left'}}>Unit Price</th>
                                                    <th className="Polaris-DataTable__Cell" style={{ textAlign: 'left'}}>Final Price</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                            {lineItems.map( (line_item, key) => {
                                                    return (
                                                        <tr key={line_item.id}>
                                                            <td className="Polaris-DataTable__Cell"><Thumbnail source={line_item.image_url} /></td>
                                                            <td className="Polaris-DataTable__Cell" style={{whiteSpace: 'normal'}}>
                                                                <div>
                                                                    {line_item.title}
                                                                    {line_item.options && line_item.options.length > 0 ? ' - ' : null}
                                                                    {line_item.options ? line_item.options.map((option) => ' ' + option.value + ' ') : null}
                                                                </div>

                                                                {parseFloat(line_item.price?.discount?.total || '0') && (
                                                                    <Text  variant="bodyMd" as="span" tone="subdued">
                                                                        <DiscountFootnote discount={line_item.price?.discount} />
                                                                    </Text>
                                                                ) || null}

                                                                {parseInt(line_item.quantity) > 0 && (
                                                                    [...Array(parseInt(line_item.quantity)).keys()].map((i) => {
                                                                        if (!Array.isArray(line_item.gift_wrapped)) {
                                                                            line_item.gift_wrapped = [];
                                                                        }
                                                                        if (!Array.isArray(line_item.gift_wrap_note)) {
                                                                            line_item.gift_wrap_note = [];
                                                                        }

                                                                        if (typeof line_item.gift_wrapped[i] === 'undefined') {
                                                                            line_item.gift_wrapped[i] = 'none';
                                                                        }
                                                                        if (typeof line_item.gift_wrap_note[i] === 'undefined') {
                                                                            line_item.gift_wrap_note[i] = '';
                                                                        }

                                                                        return (
                                                                            <div key={`${line_item.id}_${i}`} style={{ marginBottom: '20px', padding: '10px', border: '1px solid #ccc', borderRadius: '5px' }}>

                                                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                                                                    <Text variant="bodyMd" as="span">{`Gift Wrap for Item ${i + 1}`}</Text>

                                                                                    <Button onClick={() => {
                                                                                        toggleGiftWrap(line_item.id, i);
                                                                                    }}>
                                                                                        {giftWrapVisible[line_item.id]?.[i] ? 'Remove' : 'Add'}
                                                                                    </Button>
                                                                                </div>

                                                                                {giftWrapVisible[line_item.id]?.[i] && (
                                                                                    <>
                                                                                        <Select
                                                                                            id={`gift_wrapped_${line_item.id}_${i}`}
                                                                                            label="Type"
                                                                                            options={[
                                                                                                { label: 'Flat-Packed', value: 'flat-packed' },
                                                                                                { label: 'Gift-Wrapped', value: 'gift-wrapped' },
                                                                                            ]}
                                                                                            value={line_item.gift_wrapped?.[i] === 'none' ? 'flat-packed' : line_item.gift_wrapped?.[i]}
                                                                                            onChange={(value) => updateLineItemWrapAndNote(line_item.id, i, 'gift_wrapped', value)}
                                                                                            style={{ marginTop: '10px' }}
                                                                                        />
                                                                                        <TextField
                                                                                            multiline={3}
                                                                                            label="Note"
                                                                                            value={line_item.gift_wrap_note?.[i] || ''}
                                                                                            onChange={(value) => updateLineItemWrapAndNote(line_item.id, i, 'gift_wrap_note', value)}
                                                                                            style={{ marginTop: '10px' }}
                                                                                        />
                                                                                        <div style={{ color: 'red', marginTop: '5px', minHeight: '20px', visibility: notesErrors[line_item.id]?.[i] ? 'visible' : 'hidden' }}>
                                                                                            Special characters are not allowed.
                                                                                        </div>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })
                                                                )}
                                                            </td>
                                                            <td className="Polaris-DataTable__Cell">
                                                                <Text variant="headingMd" as="h2">{line_item.inventory_quantity}</Text>
                                                            </td>
                                                            <td className="Polaris-DataTable__Cell">
                                                                <TextField value={line_item.quantity}
                                                                           min={0}
                                                                           max={line_item.inventory_quantity}
                                                                           step={line_item.increment_quantity_by ?? 1}
                                                                           type="number"
                                                                           onChange={quantity => updateQuantity(key, quantity)}
                                                                />
                                                            </td>
                                                            <td className="Polaris-DataTable__Cell">
                                                                <FinalPrice lineItem={line_item}>
                                                                    <TextField type="number"
                                                                               prefix="$"
                                                                               value={(line_item.price.manual === null) ? line_item.price.discounted : line_item.price.manual}
                                                                               min="0"
                                                                               max={line_item.price.unit}
                                                                               onChange={price => updateManualPrice(key, price)}/>
                                                                </FinalPrice>
                                                            </td>
                                                            <td className="Polaris-DataTable__Cell">
                                                                <Text variant="headingMd" as="h2">${(parseFloat(line_item.price.total) - parseFloat(line_item?.price?.discount?.total ?? 0)).toFixed(2)}</Text>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <td className="Polaris-DataTable__Cell" colSpan="4">
                                                        <Text variant="headingMd" as="h2">
                                                            {lineItems.reduce((acc, item) => acc + (parseFloat(item.price.unit) * parseInt(item.quantity, 10)), 0.0) - totalPrice > 0
                                                                ? "Total Before Discounts"
                                                                : "Total"}
                                                        </Text>
                                                    </td>
                                                    <td className="Polaris-DataTable__Cell">
                                                        <Text variant="headingMd" as="h2">
                                                            {`$${lineItems.reduce((acc, item) => {
                                                                return acc + (parseFloat(item.price.unit) * parseInt(item.quantity, 10));
                                                            }, 0.0).toFixed(2)}`}
                                                        </Text>
                                                    </td>
                                                </tr>

                                                {lineItems.reduce((acc, item) => acc + (parseFloat(item.price.unit) * parseInt(item.quantity, 10)), 0.0) - totalPrice > 0 && (
                                                    <>
                                                        <tr>
                                                            <td className="Polaris-DataTable__Cell" colSpan="4">
                                                                <Text variant="headingMd" as="h2">Discount</Text>
                                                            </td>
                                                            <td className="Polaris-DataTable__Cell">
                                                                <Text variant="headingMd" as="h2">
                                                                    {`-$${Math.max(0, (lineItems.reduce((acc, item) => {
                                                                        return acc + (parseFloat(item.price.unit) * parseInt(item.quantity, 10));
                                                                    }, 0.0) - totalPrice)).toFixed(2)}`}
                                                                </Text>
                                                            </td>
                                                        </tr>

                                                        <tr>
                                                            <td className="Polaris-DataTable__Cell" colSpan="4">
                                                                <Text variant="headingMd" as="h2">Total After Discounts</Text>
                                                            </td>
                                                            <td className="Polaris-DataTable__Cell">
                                                                <Text variant="headingMd" as="h2">
                                                                    {`$${parseFloat(totalPrice).toFixed(2)}`}
                                                                </Text>
                                                            </td>
                                                        </tr>
                                                    </>
                                                )}

                                            </tfoot>
                                        </table>
                                        {giftProduct && (
                                            <div style={{ width: '100%', margin: '10px 0' }}>
                                                {eligibleForGift ? (
                                                    <Banner title="Gift Available!" tone="success">
                                                        <p>
                                                            Your order qualifies for a gift: <strong>{giftProduct.title}</strong>.
                                                        </p>
                                                    </Banner>
                                                ) : (
                                                    <Banner title="Gift Not Yet Available" tone="info">
                                                        <p>
                                                            Add <strong>${(threshold - totalPrice <= 0.01 ? '0.01' : (threshold - totalPrice).toFixed(2))}</strong> more to your order to qualify for a gift: <strong>{giftProduct.title}</strong>.
                                                        </p>
                                                    </Banner>
                                                )}
                                            </div>
                                        )}
                                    </LegacyCard.Section>
                                </>
                            )}
                            {hasMoreResults && (
                                <LegacyCard.Section>
                                    <Banner tone="info">
                                        <p>There are more matching products. Please narrow down the search query.</p>
                                    </Banner>
                                </LegacyCard.Section>
                            )}
                        </LegacyCard>
                    </Layout.Section>
                    {lineItems.length > 0 && (<>
                        <Layout.Section>
                            <LegacyCard sectioned title="Discount Code">
                                <FormLayout>
                                    <LegacyStack>
                                            <TextField
                                                value={discountCode.code}
                                                onChange={(code) => setDiscountCode(state => ({
                                                    ...state,
                                                    code,
                                                    requires_refresh: true
                                                }))}
                                                disabled={discountCode.applied}
                                            />
                                            {discountCode.applied ? (
                                                <Button
                                                    onClick={removeDiscountCode}
                                                    loading={discountCode.loading}
                                                    variant="destructive"
                                                >
                                                    Remove
                                                </Button>
                                            ) : (
                                                <Button
                                                    onClick={applyDiscountCode}
                                                    loading={discountCode.loading}
                                                    variant="primary"
                                                    disabled={!discountCode.code}
                                                >
                                                    Apply
                                                </Button>
                                            )}
                                        </LegacyStack>
                                </FormLayout>
                            </LegacyCard>
                        </Layout.Section>
                    </>)}
                    <Layout.Section>
                        <LegacyCard sectioned title="Additional Information">
                            <FormLayout>
                                <TextField multiline={3} label="Order Notes" value={additionalInfo.order_notes} id="order_notes" onChange={changeAdditionalInfo} />
                            </FormLayout>
                        </LegacyCard>
                    </Layout.Section>
                    { Object.keys(errors).length > 0 && (
                        <Layout.Section>
                        <Banner
                            title="There were some errors creating the order."
                            tone="critical"
                            >
                                <List>
                                    {Object.keys(errors).map(key => <List.Item><strong>{key.replace('_', ' ')}</strong>: {errors[key]}</List.Item>)}
                                </List>
                            </Banner>
                        </Layout.Section>
                    )}
                    {discountCode.requires_refresh && discountCode.code && (
                        <Layout.Section>
                            <Banner tone="critical">
                                <p>The discount code requires a refresh. Please re-apply the discount before placing the order.</p>
                            </Banner>
                        </Layout.Section>
                    )}

                    <CustomerOnHold customer={customer}/>
                </Layout>
                <PageActions
                    primaryAction={
                        isCalculateVisible
                            ? {
                                content: 'Calculate',
                                onAction: calculateOrder,
                                loading: calculating,
                            }
                            : isPlaceOrderVisible
                            ? {
                                content: 'Place Order and Review',
                                onAction: placeAndReview,
                                loading: creating,
                            }
                            : undefined
                    }
                />
            </Page>
        </Frame>
    );
}

export default Orders;
